import BrandThemeProvider from "@casino/legacy/client/components/Auth/Shared/BrandThemeProvider";
import useSite from "@casino/legacy/client/hooks/useSite";
import FormCaptcha from "@casino/legacy/client/shared/form/FormCaptcha";
import FormCheckbox from "@casino/legacyUi/form/FormCheckbox";
import DateInput from "@casino/legacyUi/form/FormDateInput/DateInput";
import FormError from "@casino/legacyUi/form/FormError";
import FormInput from "@casino/legacyUi/form/FormInput";
import { FormItem } from "@casino/legacyUi/form/FormItem";
import FormPhoneNumber from "@casino/legacyUi/form/FormPhoneNumber";
import type { ReactNode } from "react";
import { Controller } from "react-hook-form";
import EmailSuggestion from "./EmailSuggestion";
import LocationBlockedToast from "./LocationBlockedToast";
import Submit from "./Submit";
import useFormRegister from "./useFormRegister";

const excludedPhoneCountries: Partial<SiteRecord<string[]>> = {
  bitcasino: ["NO"],
};

type Props = {
  isPopover?: boolean;
  onSubmitHandle?: () => void;
};

const FormRegister: FC<Props> = ({ onSubmitHandle, isPopover = false }) => {
  const {
    control,
    offersLabel,
    termsLabel,
    errors,
    loading,
    isCaptchaRequired,
    recaptchaRef,
    showEmailSuggestion,
    required,
    optional,
    handleSubmit,
    setValue,
    getValues,
    handleSelectedSuggestionEmail,
    usernameInputOptions,
    emailInputOptions,
    acceptsTerms,
    offers,
    clearErrors,
  } = useFormRegister({ onSuccess: onSubmitHandle, isPopover });
  const { site } = useSite();

  const handleOffersClick = () => {
    const offers = !getValues().offers;
    setValue("offersCall", offers);
    setValue("offersSms", offers);
  };
  const handleAcceptsTermsClick = () => {
    const acceptsTerms = getValues().acceptsTerms;
    setValue("acceptsTerms", !acceptsTerms);
  };

  const isLocationBlocked =
    errors.global?.message === "error.NOT_ALLOWED_TO_USE_SITE";
  let globalError: ReactNode;
  if (isLocationBlocked) {
    globalError = (
      <LocationBlockedToast onClose={() => clearErrors("global")} />
    );
  } else {
    globalError = <FormError message={errors.global?.message ?? ""} />;
  }

  return (
    <form>
      <div className="grid gap-2 list-none">
        <FormInput
          {...required("username", true, usernameInputOptions)}
          type="text"
          label="profile.username"
          autoCapitalize="off"
          error={errors.username?.message}
          className="fs-mask"
        />
        <FormInput
          {...required("email", true, emailInputOptions)}
          type="email"
          label="profile.email"
          autoCapitalize="off"
          error={errors.email?.message}
          className="fs-mask"
        />
        {showEmailSuggestion && (
          <EmailSuggestion
            handleSelectedSuggestionEmail={handleSelectedSuggestionEmail}
          />
        )}
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { onChange, onBlur } }) => (
            <FormItem>
              <DateInput
                name="dateOfBirth"
                error={errors.dateOfBirth?.day?.message}
                adultOnly
                onChange={onChange}
                onBlur={onBlur}
              />
            </FormItem>
          )}
        />
        <FormPhoneNumber
          control={control}
          {...optional("phone")}
          name="phone"
          label="profile.phone.optional"
          error={errors.phone?.message}
          excludedCountries={excludedPhoneCountries[site]}
        />
        <FormInput
          {...required("password")}
          type="password"
          label="profile.password"
          error={errors.password?.message}
          className="fs-mask"
        />
        <BrandThemeProvider hasBombayClubTheme={isPopover}>
          <FormCheckbox
            {...required("acceptsTerms")}
            checked={acceptsTerms}
            label={termsLabel}
            error={errors.acceptsTerms?.message}
            onClick={handleAcceptsTermsClick}
          />
          <FormCheckbox
            {...optional("offers")}
            checked={offers}
            label={offersLabel}
            onClick={handleOffersClick}
          />
          {isCaptchaRequired && (
            <FormCaptcha
              {...required("captcha", !!isCaptchaRequired)}
              name="captcha"
              control={control}
              error={errors.captcha?.message}
              ref={recaptchaRef}
            />
          )}
          {globalError}
          <Submit loading={loading} handleSubmit={handleSubmit} />
        </BrandThemeProvider>
      </div>
    </form>
  );
};

export default FormRegister;
